import { ReactNode } from 'react';
import { Clock, HelpCircle, MessageCircle, Shield } from 'lucide-react';
import { AgentPage } from '@/types/agent/agent-page';
import { DEFAULT_BRANDING } from '@/types/ui/branding';

interface SupportTemplateProps {
  agent: AgentPage;
  children: ReactNode;
}

const SupportTemplate = ({ agent, children }: SupportTemplateProps) => {
  const brand = agent?.agents?.branding?.primaryColor || agent.branding?.primaryColor || agent.brand_color || DEFAULT_BRANDING.primaryColor;

  return (
    <div style={{ minHeight: '100vh', width: '100%', background: '#F9FAFC' }}>
      {/* Header Bar */}
      <div
        style={{
          background: '#ffffff',
          borderBottom: `3px solid ${brand}`,
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <HelpCircle size={32} color={brand} />
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.25rem' }}>
              {agent.name || 'Support Center'}
            </h1>
            <p style={{ fontSize: '0.9rem', color: '#6B7280' }}>How can we help you today?</p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem', marginBottom: '3rem' }}>
          {/* Quick Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}
          >
            <div
              style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${brand}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Clock size={24} color={brand} />
              </div>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937' }}>24/7</div>
                <div style={{ fontSize: '0.85rem', color: '#6B7280' }}>Always Available</div>
              </div>
            </div>

            <div
              style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${brand}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MessageCircle size={24} color={brand} />
              </div>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937' }}>Instant</div>
                <div style={{ fontSize: '0.85rem', color: '#6B7280' }}>Response Time</div>
              </div>
            </div>

            <div
              style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${brand}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Shield size={24} color={brand} />
              </div>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937' }}>Secure</div>
                <div style={{ fontSize: '0.85rem', color: '#6B7280' }}>& Confidential</div>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem', '@media (min-width: 768px)': { gridTemplateColumns: '2fr 1fr' } }}>
          {/* Main Agent Card */}
          <div>
            <div
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                padding: '40px',
                border: `2px solid ${brand}20`,
              }}
            >
              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.5rem' }}>
                  Talk to Support Assistant
                </h2>
                <p style={{ color: '#6B7280', fontSize: '0.95rem' }}>
                  {agent.description || 'Get instant help with your questions and issues'}
                </p>
              </div>
              {children}
            </div>
          </div>

          {/* Sidebar - Common Issues */}
          <div>
            <div
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: '1.5rem',
              }}
            >
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1F2937', marginBottom: '1rem' }}>
                Common Questions
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  'How do I reset my password?',
                  'Where can I find my account info?',
                  'How do I update my settings?',
                  'What are your support hours?',
                  'How do I contact a human?',
                ].map((question, idx) => (
                  <button
                    key={idx}
                    style={{
                      textAlign: 'left',
                      padding: '0.75rem',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      color: '#374151',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = `${brand}10`;
                      e.currentTarget.style.borderColor = brand;
                      e.currentTarget.style.color = brand;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                      e.currentTarget.style.color = '#374151';
                    }}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Options */}
            <div
              style={{
                background: `${brand}10`,
                borderRadius: '16px',
                border: `1px solid ${brand}30`,
                padding: '1.5rem',
                marginTop: '1.5rem',
              }}
            >
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: brand, marginBottom: '0.75rem' }}>
                Need More Help?
              </h3>
              <p style={{ color: '#374151', fontSize: '0.85rem', lineHeight: '1.6' }}>
                Our support team is here to help you with any questions or concerns you may have.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          background: '#ffffff',
          borderTop: '1px solid #E5E7EB',
          padding: '40px 20px',
          marginTop: '60px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>
            Available 24/7 • Secure Support • Fast Response
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportTemplate;
